import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const DB_MODE_KEY = 'dbMode';
const DEMO_MODE_KEY = 'demoMode';
const ALLOW_SWITCH = process.env.REACT_APP_ALLOW_DB_SWITCH === 'true';
const ALLOW_DEMO = process.env.REACT_APP_ALLOW_DEMO_MODE === 'true';

// ── Intercepteur axios global ─────────────────────────────────────────────────
// Lit directement localStorage à chaque requête → réactif sans état React.
// Gère les deux modes (dev & démo) avec le header X-DB-Mode approprié.
if (ALLOW_SWITCH || ALLOW_DEMO) {
  axios.interceptors.request.use((config) => {
    if (ALLOW_SWITCH && localStorage.getItem(DB_MODE_KEY) === 'dev') {
      config.headers = config.headers || {};
      config.headers['X-DB-Mode'] = 'dev';
    } else if (ALLOW_DEMO && localStorage.getItem(DEMO_MODE_KEY) === 'demo') {
      config.headers = config.headers || {};
      config.headers['X-DB-Mode'] = 'demo';
    }
    return config;
  });
}
// ─────────────────────────────────────────────────────────────────────────────

const DbModeContext = createContext({
  dbMode: 'prod',
  setDbMode: () => {},
  allowSwitch: false,
  demoMode: 'real',
  setDemoMode: () => {},
  allowDemo: false,
});

export function DbModeProvider({ children }) {
  const [dbMode, setDbModeState] = useState(() => {
    if (!ALLOW_SWITCH) return 'prod';
    return localStorage.getItem(DB_MODE_KEY) || 'prod';
  });

  const [demoMode, setDemoModeState] = useState(() => {
    if (!ALLOW_DEMO) return 'real';
    return localStorage.getItem(DEMO_MODE_KEY) || 'real';
  });

  const originalFetch = useRef(null);

  useEffect(() => {
    if (!ALLOW_SWITCH && !ALLOW_DEMO) return;

    if (!originalFetch.current) {
      originalFetch.current = window.fetch.bind(window);
    }
    const orig = originalFetch.current;

    const activeMode =
      (ALLOW_SWITCH && dbMode === 'dev') ? 'dev'
      : (ALLOW_DEMO && demoMode === 'demo') ? 'demo'
      : null;

    if (activeMode) {
      window.fetch = (url, opts = {}) => {
        const urlStr =
          typeof url === 'string' ? url
          : url instanceof Request ? url.url
          : String(url);
        if (urlStr.includes('/api/')) {
          const headers = new Headers(opts.headers || {});
          headers.set('X-DB-Mode', activeMode);
          return orig(url, { ...opts, headers });
        }
        return orig(url, opts);
      };
    } else {
      window.fetch = orig;
    }

    return () => {
      if (originalFetch.current) window.fetch = originalFetch.current;
    };
  }, [dbMode, demoMode]);

  const setDbMode = (mode) => {
    if (!ALLOW_SWITCH) return;
    localStorage.setItem(DB_MODE_KEY, mode);
    setDbModeState(mode);
    window.location.reload();
  };

  const setDemoMode = (mode) => {
    if (!ALLOW_DEMO) return;
    localStorage.setItem(DEMO_MODE_KEY, mode);
    setDemoModeState(mode);
    window.location.reload();
  };

  return (
    <DbModeContext.Provider value={{
      dbMode, setDbMode, allowSwitch: ALLOW_SWITCH,
      demoMode, setDemoMode, allowDemo: ALLOW_DEMO,
    }}>
      {children}
    </DbModeContext.Provider>
  );
}

export function useDbMode() {
  return useContext(DbModeContext);
}
